// Quick-compare scan orchestration for product refresh (KAN-1464).
//
// Extracted from UpdateProductModal.tsx's pollQuickCompareStatus loop
// (~lines 999-1117) and its verdict routing switch (~lines 1047-1080).
// This module owns no UI state; it only starts the WorkProductProcessor
// quick-compare job, polls it to a terminal state, and classifies the
// result into a RefreshVerdict.

import { getCompareStatus, startQuickCompare } from "@/api/api";

import type { RefreshVerdict, ScanOutcome } from "./types";

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_TIMEOUT_MS = 300000;

/**
 * Classify a quick-compare status payload into a RefreshVerdict.
 *
 * Mirrors UpdateProductModal.tsx's routing switch: the wire value
 * "COMPLETED" (on quick_compare_status) maps to MINOR_EVOLUTION, and when
 * quick_compare_status is missing/unrecognized we fall back to
 * scope_assessment.verdict === "MINOR_EVOLUTION" for backward compatibility.
 * Anything else classifies as null (caller decides how to handle it).
 */
export function classifyVerdict(
    statusPayload: { quick_compare_status?: string; scope_assessment?: { verdict?: string } } | null | undefined
): RefreshVerdict | null {
    if (!statusPayload) {
        return null;
    }

    switch (statusPayload.quick_compare_status) {
        case "NO_CHANGES":
            return "NO_CHANGES";
        case "DRASTIC_CHANGE":
            return "DRASTIC_CHANGE";
        case "REFINEMENT_ONLY":
            return "REFINEMENT_ONLY";
        case "COMPLETED":
            return "MINOR_EVOLUTION";
        default:
            if (statusPayload.scope_assessment?.verdict === "MINOR_EVOLUTION") {
                return "MINOR_EVOLUTION";
            }
            return null;
    }
}

export interface RunRefreshScanArgs {
    token: string;
    projectId: number;
    userId: number;
    originalAnswers: unknown[];
    newAnswers: unknown[];
    originalDescription: string;
    onPhase?: (phase: string, message?: string) => void;
    pollIntervalMs?: number;
    timeoutMs?: number;
}

/**
 * Start a quick-compare job and poll it to completion, returning the
 * classified verdict plus the raw comparison_result payload.
 *
 * Terminal conditions (from the modal's poll loop):
 * - status === "COMPLETED": resolve with the classified verdict + result.
 * - status === "FAILED": reject with an Error carrying status.error.
 * - otherwise: keep polling every pollIntervalMs until timeoutMs elapses,
 *   at which point the promise rejects with a timeout error.
 *
 * Note: onPhase is invoked unconditionally every poll (including when phase
 * is undefined), mirroring the modal's phase setter. Rollback POST and dialog
 * orchestration on FAILED are intentionally not extracted here — those land
 * in later tasks.
 */
export async function runRefreshScan(args: RunRefreshScanArgs): Promise<ScanOutcome> {
    const {
        token,
        projectId,
        userId,
        originalAnswers,
        newAnswers,
        originalDescription,
        onPhase,
        pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
        timeoutMs = DEFAULT_TIMEOUT_MS
    } = args;

    const { job_id: jobId } = await startQuickCompare(
        {
            projectid: projectId,
            userid: userId,
            original_answers: originalAnswers,
            new_answers: newAnswers,
            original_description: originalDescription
        },
        token
    );

    const deadline = Date.now() + timeoutMs;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const status = await getCompareStatus(jobId, token);

        onPhase?.(status.phase ?? "", status.message);

        if (status.status === "COMPLETED") {
            const result = status.comparison_result;
            const verdict = classifyVerdict(result);
            if (!verdict) {
                // classifyVerdict returns null for unrecognized payloads by contract.
                // Preserve the modal's fallback behavior: unrecognized/missing
                // verdicts are treated as DRASTIC_CHANGE by the caller's dialog
                // routing, but classifyVerdict itself only reports what it can
                // determine. runRefreshScan surfaces null verdicts as DRASTIC_CHANGE
                // to match UpdateProductModal.tsx:1071-1078's default branch.
                return { verdict: "DRASTIC_CHANGE", result };
            }
            return { verdict, result };
        }

        if (status.status === "FAILED") {
            throw new Error(status.error || "Quick compare failed");
        }

        if (Date.now() >= deadline) {
            throw new Error(`Quick compare scan timed out after ${timeoutMs}ms`);
        }

        await sleep(pollIntervalMs);

        if (Date.now() >= deadline) {
            throw new Error(`Quick compare scan timed out after ${timeoutMs}ms`);
        }
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}
